import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import blueWave from "../../assets/blue_wave.mp4";
import { useIsUserSignedIn } from "../../core_stores/useAuthStore";
import {
  SideMenuBehavior,
  useSideMenuBehavior,
} from "../../view_stores/useSideMenuStore";
import SideMenuPanel from "../sideMenu/SideMenuPanel";
import { WelcomeAppBar } from "../welcome/components/WelcomeAppBar";
import { WelcomeFooter } from "../welcome/components/WelcomeFooter";
import { BlogPostCard } from "./components/BlogPostCard";

interface BlogPostMeta {
  slug: string;
  title: string;
  date: string;
  excerpt: string;
  coverImage: string;
  dateFolder: string;
}

export function BlogListPage() {
  const [posts, setPosts] = useState<BlogPostMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isUserSignedIn = useIsUserSignedIn();
  const sideMenuBehavior = useSideMenuBehavior();

  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        const response = await fetch("/blog/index.json");
        if (!response.ok) {
          throw new Error("Failed to load blog posts");
        }
        const data = await response.json();
        setPosts(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPosts();
  }, []);

  return (
    <>
      <Helmet>
        <title>Blog | Darvin - AI-Powered Mobile App Builder</title>
        <meta
          name="description"
          content="Read the latest articles about no-code mobile app development, AI-powered app creation, and tips for building Flutter apps with Darvin."
        />
        <meta property="og:title" content="Blog | Darvin" />
        <meta
          property="og:description"
          content="Read the latest articles about no-code mobile app development, AI-powered app creation, and tips for building Flutter apps with Darvin."
        />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://darvin.dev/blog" />
      </Helmet>

      <div className="min-h-screen flex">
        {isUserSignedIn && <SideMenuPanel />}
        <div
          className={`${
            isUserSignedIn &&
            (sideMenuBehavior === SideMenuBehavior.PINNED
              ? "pl-[320px]"
              : "pl-[60px]")
          } flex-1 flex flex-col transition-[padding] duration-300 relative`}
        >
          {/* Header with plasma background */}
          <div className="flex flex-col relative">
            <div className="absolute right-0 top-0 w-full h-full overflow-hidden">
              <video
                src={blueWave}
                autoPlay
                loop
                muted
                className="w-full h-full object-cover scale-[1.1]"
              />
            </div>
            <div
              className="absolute inset-0 bg-gradient-to-t
              from-light-surface-primary dark:from-dark-surface-primary via-[#03142800]
              to-transparent pointer-events-none"
            />

            <WelcomeAppBar />

            <div className="flex justify-center items-center px-8 pt-8 pb-10 md:pb-16 z-10">
              <div className="w-full max-w-[1300px] text-center">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4">
                  Darvin Blog
                </h1>
                <p className="text-lg md:text-xl lg:text-2xl text-white/90 max-w-4xl mx-auto">
                  Tutorials, guides, and insights on building mobile apps with
                  AI, prompt engineering, and no-code development
                </p>
              </div>
            </div>
          </div>

          {/* Blog Posts Grid */}
          <div
            className="flex-1 flex flex-col
            bg-light-surface-primary dark:bg-dark-surface-primary px-8"
          >
            <div className="w-full max-w-[1300px] mx-auto py-8 md:py-12">
              {loading && (
                <div className="text-center py-12">
                  <p className="text-gray-600 dark:text-gray-300">
                    Loading posts...
                  </p>
                </div>
              )}

              {error && (
                <div className="text-center py-12">
                  <p className="text-red-600 dark:text-red-400">
                    Error: {error}
                  </p>
                </div>
              )}

              {!loading && !error && posts.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-600 dark:text-gray-300">
                    No blog posts available yet.
                  </p>
                </div>
              )}

              {!loading && !error && posts.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {posts.map((post) => (
                    <BlogPostCard
                      key={post.slug}
                      slug={post.slug}
                      title={post.title}
                      excerpt={post.excerpt}
                      coverImage={post.coverImage}
                      date={post.date}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <WelcomeFooter />
        </div>
      </div>
    </>
  );
}
